import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Calendar, AlertCircle, XCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const SubscriptionStatus: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Fetch subscription status
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['/api/payments/subscription-status'],
    queryFn: async () => {
      const response = await api.get('/api/payments/subscription-status');
      return response.data;
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      console.log('Starting cancel subscription request...');
      try {
        const response = await api.post('/api/payments/cancel-subscription');
        console.log('Cancel subscription response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Cancel subscription error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Subscription canceled successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/payments/subscription-status'] });
      toast({
        title: 'Subscription Canceled',
        description: `Your subscription has been canceled. You'll retain access until ${new Date(data.ends_at).toLocaleDateString()}.`,
      });
      setShowCancelDialog(false);
    },
    onError: (error: any) => {
      console.error('Cancel subscription mutation error:', {
        error,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Failed to cancel subscription. Please try again.';
      
      toast({
        title: 'Cancellation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });


  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Active</Badge>;
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>;
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>;
      default:
        return <Badge variant="outline">Inactive</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription
        </CardTitle>
        <CardDescription>
          Manage your subscription and billing
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {subscription?.has_active_subscription ? (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              {getStatusBadge(subscription.status)}
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Plan</span>
              <span className="font-medium capitalize">
                {subscription.plan === 'annual' ? 'Annual ($72/month)' : 'Monthly ($90/month)'}
              </span>
            </div>
            
            {subscription.ends_at && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  <Calendar className="inline h-3 w-3 mr-1" />
                  {subscription.status === 'canceled' ? 'Ends on' : 'Renews on'}
                </span>
                <span className="font-medium">{formatDate(subscription.ends_at)}</span>
              </div>
            )}

            {subscription.status === 'past_due' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">Payment failed</p>
                  <p className="mt-1">Please update your payment method to continue using Bloom.</p>
                </div>
              </div>
            )}

            {subscription.status === 'canceled' ? (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Subscription canceled</p>
                    <p className="mt-1">You'll retain access until {formatDate(subscription.ends_at)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowCancelDialog(true)}
                className="w-full"
                variant="destructive"
                disabled={cancelSubscriptionMutation.isPending}
              >
                {cancelSubscriptionMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Canceling...
                  </>
                ) : (
                  <>
                    <XCircle className="mr-2 h-4 w-4" />
                    Cancel Subscription
                  </>
                )}
              </Button>
            )}
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-muted-foreground mb-4">
              No active subscription
            </p>
            <Button 
              onClick={() => window.location.href = '/pricing'} 
              className="w-full"
            >
              Subscribe Now
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Cancel Subscription Dialog */}
    <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to cancel your subscription?
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-blue-800">
                  Important information:
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• You'll retain access until {subscription?.ends_at ? formatDate(subscription.ends_at) : 'the end of your billing period'}</li>
                  <li>• Your player data and shortlists will be preserved</li>
                  <li>• You can resubscribe anytime to regain access</li>
                  <li>• No refunds will be issued for the remaining period</li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                If you're experiencing issues, please contact support before canceling.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              console.log('Cancel subscription button clicked');
              cancelSubscriptionMutation.mutate();
            }}
            disabled={cancelSubscriptionMutation.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {cancelSubscriptionMutation.isPending ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};